import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Suspense, lazy, useEffect, useState } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { ErrorState } from '@/components/States';
import { AppShell } from '@/layouts/AppShell';
import { AuthBoundary } from '@/layouts/AuthBoundary';
import { initialiseAuth, type Session } from '@/auth/keycloak';
import { loadConfig } from '@/lib/config';
import { registerVisit } from '@/lib/users-api';
import { ApiError } from '@/lib/api-client';
import { CampaignsPage } from '@/pages/CampaignsPage';
import { ContactsPage } from '@/pages/ContactsPage';
import { DeliverabilityPage } from '@/pages/DeliverabilityPage';
import { ReportPage } from '@/pages/ReportPage';
import { AudiencePage } from '@/pages/AudiencePage';
import { SegmentsPage } from '@/pages/SegmentsPage';
import { SendPage } from '@/pages/SendPage';
import { BrandsPage } from '@/pages/settings/BrandsPage';
import { SendersPage } from '@/pages/settings/SendersPage';
import { IntegrationsPage } from '@/pages/settings/IntegrationsPage';
import { TopicsPage } from '@/pages/settings/TopicsPage';
import { UserGuidePage } from '@/pages/UserGuidePage';
import { Skeleton } from '@/components/States';
import { useTheme } from '@/theme/use-theme';

/**
 * The workspace is lazy because Monaco is ~2MB of the bundle and only this route needs it. The
 * campaigns list is where people land, and it should not pay for an editor they may not open.
 */
const WorkspacePage = lazy(() =>
  import('@/pages/WorkspacePage').then((module) => ({ default: module.WorkspacePage })),
);

/**
 * Defaults tuned for an admin tool rather than a consumer feed.
 *
 * `refetchOnWindowFocus: false` specifically: the workspace holds unsaved editor state, and a
 * refetch triggered by alt-tabbing back from a browser could replace what someone is typing with
 * the server's copy. That is the single most annoying thing a data layer can do to an editor.
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      staleTime: 30_000,
      retry: (failureCount, error) => {
        // Never retry a 4xx — the role will not change, the resource will not appear, and the
        // validation error will not pass on a second identical attempt.
        if (error instanceof ApiError && error.status >= 400 && error.status < 500) return false;

        return failureCount < 2;
      },
    },
    mutations: { retry: false },
  },
});

type BootState =
  | { status: 'loading' }
  | { status: 'ready'; session: Session }
  | { status: 'error'; error: unknown };

export function App() {
  useTheme();
  const [boot, setBoot] = useState<BootState>({ status: 'loading' });

  // Synchronising with something outside React — fetching config and initialising an external auth
  // library, both of which must happen exactly once before anything renders.
  useEffect(() => {
    let cancelled = false;

    async function start() {
      try {
        await loadConfig();

        // A hard ceiling on boot. Anything that hangs here — an unreachable Keycloak, a blocked
        // iframe, a DNS stall — would otherwise leave a permanently blank page, because App renders
        // null while loading. Ten seconds is far longer than a healthy boot and far shorter than a
        // user's patience with a white screen.
        const session = await Promise.race([
          initialiseAuth(),
          new Promise<never>((_, reject) =>
            setTimeout(
              () =>
                reject(
                  new Error(
                    'Hazcheck sign-in timed out. Try again or contact your administrator if it continues.',
                  ),
                ),
              10_000,
            ),
          ),
        ]);

        if (cancelled) return;

        setBoot({ status: 'ready', session });

        // Register in the directory so colleagues can pick this person as an approver. Deliberately
        // NOT awaited and deliberately swallowed: it is a write nobody is waiting on, and somebody
        // who cannot be registered can still do their whole job. Blocking boot on it, or surfacing a
        // banner about it, would be a lot of noise about a background detail (see users-api.ts).
        void registerVisit().catch(() => undefined);
      } catch (error) {
        // A Keycloak that is unreachable must not produce a blank page. It is the single most
        // likely thing to be down in local development, since it lives in another repo.
        if (!cancelled) setBoot({ status: 'error', error });
      }
    }

    void start();

    return () => {
      cancelled = true;
    };
  }, []);

  if (boot.status === 'loading') {
    // Deliberately nothing: boot resolves in tens of milliseconds against a local Keycloak, and a
    // spinner that flashes for 80ms makes the app feel slower than no spinner at all.
    return null;
  }

  if (boot.status === 'error') {
    return (
      <main className="flex min-h-dvh items-center justify-center bg-bg px-6">
        <div className="w-full max-w-lg rounded-lg border border-border bg-surface p-2 shadow-md">
          <ErrorState error={boot.error} onRetry={() => window.location.reload()} />

          <p className="px-6 pb-6 text-center text-xs text-muted">
            Campaigns uses your HazcheckHub account. If sign-in stays unavailable, contact your
            administrator.
          </p>
        </div>
      </main>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthBoundary session={boot.session}>
          <Routes>
            <Route element={<AppShell session={boot.session} />}>
              <Route path="/" element={<Navigate to="/campaigns" replace />} />
              <Route path="/campaigns" element={<CampaignsPage />} />
              <Route path="/contacts" element={<ContactsPage />} />
              <Route path="/segments" element={<SegmentsPage />} />
              <Route path="/deliverability" element={<DeliverabilityPage />} />
              <Route path="/campaigns/:campaignId/audience" element={<AudiencePage />} />
              <Route
                path="/campaigns/:campaignId/send"
                element={<SendPage session={boot.session} />}
              />
              <Route
                path="/campaigns/:campaignId/report"
                element={<ReportPage session={boot.session} />}
              />

              {/* Two routes onto one page: /user-guide opens at the first article, and
                  /user-guide/:slug is the deep link somebody was sent. */}
              <Route path="/user-guide" element={<UserGuidePage session={boot.session} />} />
              <Route path="/user-guide/:slug" element={<UserGuidePage session={boot.session} />} />

              {/* Kept as a redirect rather than deleted: the nav used to point here, and a bookmark
                  landing on a 404 is worse than one landing on the page that replaced it. */}
              <Route path="/audience" element={<Navigate to="/contacts" replace />} />
              <Route path="/reports" element={<Navigate to="/campaigns" replace />} />
              {/* Three routes, not one page with three sections: brand identity, the addresses mail
                  goes out as, and what recipients can opt into are three different jobs on three
                  different days. /settings/domains was a fourth until domain authentication came out —
                  it redirects rather than 404s, because the nav pointed there. */}
              <Route path="/settings" element={<Navigate to="/settings/brands" replace />} />
              <Route
                path="/settings/domains"
                element={<Navigate to="/settings/brands" replace />}
              />
              <Route path="/settings/brands" element={<BrandsPage session={boot.session} />} />
              <Route path="/settings/senders" element={<SendersPage session={boot.session} />} />
              <Route path="/settings/topics" element={<TopicsPage session={boot.session} />} />
              <Route
                path="/settings/integrations"
                element={<IntegrationsPage session={boot.session} />}
              />
            </Route>

            {/* Outside the shell: the workspace is a full-bleed three-pane layout and the nav rail
                would take 240px from the editor. */}
            <Route
              path="/campaigns/:campaignId"
              element={
                // A skeleton shaped like the workspace, so the panes do not jump into place when
                // the chunk arrives.
                <Suspense
                  fallback={
                    <div className="flex h-dvh flex-col gap-2 bg-bg p-4">
                      <Skeleton className="h-12 w-full" />
                      <div className="flex flex-1 gap-2">
                        <Skeleton className="h-full flex-1" />
                        <Skeleton className="h-full flex-1" />
                      </div>
                    </div>
                  }
                >
                  <WorkspacePage />
                </Suspense>
              }
            />

            <Route path="*" element={<Navigate to="/campaigns" replace />} />
          </Routes>
        </AuthBoundary>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
